import { useEffect, useState } from 'react'
import Navbar from '../components/layout/Navbar'
import { dashboardApi } from '../api/dashboard'

const fmtDate = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: '2-digit',
    hour: 'numeric', minute: '2-digit',
  })
}

const StatCard = ({ label, value, sub, color }) => (
  <div style={{ ...styles.card, borderTop: `3px solid ${color || '#e0dfd8'}` }}>
    <div style={styles.cardLabel}>{label}</div>
    <div style={{ ...styles.cardValue, color: color || '#1a1a1a' }}>{value ?? '—'}</div>
    {sub && <div style={styles.cardSub}>{sub}</div>}
  </div>
)

const SectionTitle = ({ children }) => (
  <h2 style={styles.sectionTitle}>{children}</h2>
)

export default function DashboardPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    dashboardApi.getSummary()
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div style={styles.root}>
      <Navbar />
      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Dashboard</h1>
          {data && (
            <span style={styles.refreshHint}>
              Last send: {fmtDate(data.campaigns.last_send_at)}
            </span>
          )}
        </div>

        {loading && <div style={styles.empty}>Loading...</div>}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {data && (
          <>
            {/* Pipeline Overview */}
            <SectionTitle>Pipeline Overview</SectionTitle>
            <div style={styles.grid}>
              <StatCard
                label="Total Prospects"
                value={data.prospects.total.toLocaleString()}
                color="#1a1a1a"
              />
              <StatCard
                label="Have Email"
                value={data.prospects.has_email.toLocaleString()}
                sub={`${data.prospects.no_email} with no email`}
                color="#185FA5"
              />
              <StatCard
                label="Verified Emails"
                value={data.prospects.verified.toLocaleString()}
                sub="Safe to campaign"
                color="#0F6E56"
              />
              <StatCard
                label="Risky Emails"
                value={data.prospects.risky.toLocaleString()}
                sub="Generic inboxes"
                color="#B45309"
              />
              <StatCard
                label="Invalid Emails"
                value={data.prospects.invalid.toLocaleString()}
                sub="Do not send"
                color="#991F1F"
              />
              <StatCard
                label="No Email Found"
                value={data.prospects.no_email.toLocaleString()}
                sub="Needs scraping"
                color="#888"
              />
            </div>

            {/* Enrichment Health */}
            <SectionTitle>Enrichment Health</SectionTitle>
            <div style={styles.healthBar}>
              {data.prospects.has_email > 0 && (() => {
                const total = data.prospects.has_email
                const verified = data.prospects.verified
                const risky = data.prospects.risky
                const invalid = data.prospects.invalid
                const unverified = total - verified - risky - invalid
                return (
                  <>
                    <div style={{ ...styles.healthSegment, width: `${(verified/total*100).toFixed(1)}%`, background: '#0F6E56' }} title={`Verified: ${verified}`} />
                    <div style={{ ...styles.healthSegment, width: `${(risky/total*100).toFixed(1)}%`, background: '#B45309' }} title={`Risky: ${risky}`} />
                    <div style={{ ...styles.healthSegment, width: `${(invalid/total*100).toFixed(1)}%`, background: '#991F1F' }} title={`Invalid: ${invalid}`} />
                    <div style={{ ...styles.healthSegment, width: `${(unverified/total*100).toFixed(1)}%`, background: '#e0dfd8' }} title={`Unverified: ${unverified}`} />
                  </>
                )
              })()}
            </div>
            <div style={styles.healthLegend}>
              <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#0F6E56' }} />Verified</span>
              <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#B45309' }} />Risky</span>
              <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#991F1F' }} />Invalid</span>
              <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#e0dfd8' }} />Unverified</span>
            </div>

            {/* Prospect Status Breakdown */}
            <SectionTitle>Prospects by Status</SectionTitle>
            <div style={styles.statusGrid}>
              {Object.entries(data.prospects.by_status).map(([status, count]) => (
                <div key={status} style={styles.statusRow}>
                  <span style={styles.statusLabel}>{status}</span>
                  <span style={styles.statusCount}>{count.toLocaleString()}</span>
                </div>
              ))}
            </div>

            {/* Campaign Performance */}
            <SectionTitle>Campaign Performance</SectionTitle>
            <div style={styles.grid}>
              <StatCard
                label="Active Campaigns"
                value={data.campaigns.total_active}
                color="#1a1a1a"
              />
              <StatCard
                label="Total Sent"
                value={data.campaigns.total_sent.toLocaleString()}
                color="#0F6E56"
              />
              <StatCard
                label="Delivery Rate"
                value={`${data.campaigns.delivery_rate}%`}
                sub={`${data.campaigns.total_failed} failed`}
                color={data.campaigns.delivery_rate >= 90 ? '#0F6E56' : '#B45309'}
              />
              <StatCard
                label="Bounce Rate"
                value={`${data.campaigns.bounce_rate}%`}
                sub={data.campaigns.bounce_rate > 5 ? '⚠️ Above threshold' : '✅ Good'}
                color={data.campaigns.bounce_rate > 5 ? '#991F1F' : '#0F6E56'}
              />
            </div>

            {/* Last Campaign */}
            {data.campaigns.last_campaign_name && (
              <div style={styles.lastCampaign}>
                <span style={styles.lastCampaignLabel}>Last active campaign:</span>
                <span style={styles.lastCampaignName}>{data.campaigns.last_campaign_name}</span>
              </div>
            )}

            {/* SMS Performance */}
            <SectionTitle>SMS Performance</SectionTitle>
            <div style={styles.grid}>
              <StatCard
                label="Unsent Mobile Numbers"
                value={data.sms.mobile_unsent.toLocaleString()}
                sub={`of ${data.sms.mobile_available.toLocaleString()} total mobile`}
                color="#185FA5"
              />
              <StatCard
                label="Total Sent"
                value={data.sms.total_sent.toLocaleString()}
                sub={`${data.sms.total_failed} failed`}
                color="#0F6E56"
              />
              <StatCard
                label="Delivery Rate"
                value={`${data.sms.delivery_rate}%`}
                color={data.sms.delivery_rate >= 90 ? '#0F6E56' : '#B45309'}
              />
              <StatCard
                label="Total Replies"
                value={data.sms.total_replies.toLocaleString()}
                sub={`${data.sms.reply_rate}% reply rate`}
                color="#185FA5"
              />
              <StatCard
                label="Interested"
                value={data.sms.interested.toLocaleString()}
                sub="Replied YES"
                color="#0F6E56"
              />
            </div>
            {data.sms.last_send_at && (
              <div style={styles.lastCampaign}>
                <span style={styles.lastCampaignLabel}>Last SMS send:</span>
                <span style={styles.lastCampaignName}>{fmtDate(data.sms.last_send_at)}</span>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}

const styles = {
  root: { minHeight: '100vh', background: '#f5f5f3' },
  main: { maxWidth: 1100, margin: '0 auto', padding: '1.5rem' },
  pageHeader: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: '1.5rem',
  },
  pageTitle: { fontSize: 22, fontWeight: 500, color: '#1a1a1a' },
  refreshHint: { fontSize: 12, color: '#999' },
  sectionTitle: {
    fontSize: 13, fontWeight: 500, color: '#888',
    textTransform: 'uppercase', letterSpacing: 1,
    marginTop: '1.75rem', marginBottom: '0.75rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 12,
  },
  card: {
    background: '#fff',
    border: '0.5px solid #e0dfd8',
    borderRadius: 10,
    padding: '14px 16px',
  },
  cardLabel: { fontSize: 11, color: '#999', marginBottom: 6, fontWeight: 500 },
  cardValue: { fontSize: 26, fontWeight: 600, lineHeight: 1 },
  cardSub: { fontSize: 11, color: '#aaa', marginTop: 4 },
  healthBar: {
    display: 'flex', height: 12, borderRadius: 6,
    overflow: 'hidden', background: '#e0dfd8',
  },
  healthSegment: { height: '100%', transition: 'width 0.3s' },
  healthLegend: {
    display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' },
  dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  statusGrid: {
    background: '#fff',
    border: '0.5px solid #e0dfd8',
    borderRadius: 10,
    overflow: 'hidden',
  },
  statusRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '10px 16px',
    borderBottom: '0.5px solid #f0efe8',
    fontSize: 13,
  },
  statusLabel: { color: '#444' },
  statusCount: { fontWeight: 500, color: '#1a1a1a' },
  lastCampaign: {
    marginTop: 12, padding: '10px 14px',
    background: '#fff', border: '0.5px solid #e0dfd8',
    borderRadius: 8, fontSize: 13, display: 'flex', gap: 8,
  },
  lastCampaignLabel: { color: '#999' },
  lastCampaignName: { color: '#1a1a1a', fontWeight: 500 },
  empty: { textAlign: 'center', padding: '3rem', color: '#888' },
  errorBanner: {
    background: '#FCEBEB', color: '#791F1F',
    border: '0.5px solid #F09595',
    borderRadius: 8, padding: '10px 14px', fontSize: 13,
  },
}